/**
 * Utilities for working with compact grid-based ChartData
 */

import * as Sentry from '@sentry/react';

import { CANONICAL_HANDS, getHandMetadata } from '@/data/handMetadata';
import type { ChartData, HandCell, HandData, HandSegment } from '@/types/chart';

/**
 * Convert a grid cell value to HandSegment array
 */
function cellToSegments(cell: HandCell): HandSegment[] {
  // null = no data for this hand
  if (cell === null) {
    return [];
  }

  // number = single segment
  if (typeof cell === 'number') {
    return [{ value: cell }];
  }

  // string = categorical label, value doesn't matter (determined by category mapping)
  if (typeof cell === 'string') {
    return [{ value: 0, label: cell }];
  }

  // array = multi-segment
  return cell.map((seg) => (typeof seg === 'number' ? { value: seg } : { value: seg.value, label: seg.label }));
}

/**
 * Generate HandData array from ChartData grid
 * This converts the compact grid format into the expanded format needed for rendering
 */
export function getHandDataArray(chartData: ChartData): HandData[] {
  const hands: HandData[] = [];

  // Iterate through all 169 hands in canonical order
  for (const handName of CANONICAL_HANDS) {
    const metadata = getHandMetadata(handName);

    if (!metadata) {
      Sentry.logger.warn(`No metadata found for hand: ${handName}`);
      continue;
    }

    // Get cell value from hands grid
    const cell = chartData.hands[metadata.row]?.[metadata.col] ?? null;
    const segments = cellToSegments(cell);

    hands.push({
      hand: handName,
      row: metadata.row,
      col: metadata.col,
      type: metadata.type,
      subtype: metadata.subtype,
      segments,
    });
  }

  return hands;
}

/**
 * Get HandData for a specific hand from ChartData
 */
export function getHandData(chartData: ChartData, handName: string): HandData | null {
  const metadata = getHandMetadata(handName);

  if (!metadata) {
    return null;
  }

  const cell = chartData.hands[metadata.row]?.[metadata.col] ?? null;
  const segments = cellToSegments(cell);

  return {
    hand: handName,
    row: metadata.row,
    col: metadata.col,
    type: metadata.type,
    subtype: metadata.subtype,
    segments,
  };
}
